import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, CheckCircle2, XCircle, Hash, Mic, Sparkles, Loader2, AlertCircle } from "lucide-react";
import VoiceDictateButton from "@/components/wizard/VoiceDictateButton";
import { DENIAL_REASONS } from "@/lib/denialReasons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Quick-action modal for marking a PA approved or denied.
 *
 * Approved flow: captures optional PA #, logged-by, free-text note (with mic).
 * Denied flow:  captures required reason (dropdown), logged-by, extra detail
 *               (with mic + AI polish).
 */
export default function PaActionModal({ jobId, action, clientName, onClose, onDone }) {
  const [loggedBy, setLoggedBy] = useState("");
  const [paNumber, setPaNumber] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoggedBy(""); setPaNumber(""); setReason(""); setNote("");
  }, [jobId, action]);

  const isApprove = action === "approve";
  const title = isApprove ? "Mark as Approved" : "Mark as Denied";

  const submit = async () => {
    if (!isApprove && !reason) {
      toast.error("Pick a denial reason before saving.");
      return;
    }
    if (!loggedBy.trim()) {
      toast.error("'Logged by' is required so we know who recorded this.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/submissions/${jobId}/events`, {
        type: isApprove ? "pa_approved" : "pa_denied",
        logged_by: loggedBy.trim(),
        note: note.trim(),
        pa_number: isApprove ? paNumber.trim() : "",
        denial_reason: !isApprove ? reason : "",
      });
      toast.success(isApprove ? `✓ Approved — logged to timeline.` : `✗ Denied — logged to timeline.`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const chosenReason = DENIAL_REASONS.find((r) => r.code === reason);

  return (
    <div
      className="fixed inset-0 z-[95] bg-[#0B1E3A]/70 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="pa-action-modal"
    >
      <div
        className="relative w-full max-w-xl rounded-3xl bg-white shadow-[0_24px_64px_rgba(26,33,28,0.3)] border border-[#D6E3EF] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={`px-6 py-5 flex items-center justify-between gap-3 ${isApprove ? "bg-gradient-to-br from-[#E7F1FB] to-[#f4f6f1]" : "bg-gradient-to-br from-[#fbeae5] to-[#FFFDEC]"}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isApprove ? "bg-[#0A3D79] text-[#F7FAFC]" : "bg-[#c05746] text-white"}`}>
              {isApprove ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">Submission Log</div>
              <div className="font-display text-xl font-semibold text-[#0B1E3A]">{title}</div>
              {clientName && <div className="text-[13px] text-[#3A5275]">{clientName}</div>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-[#3A5275] hover:bg-white/70"
            aria-label="Close"
            data-testid="pa-action-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="text-[12px] font-semibold text-[#0B1E3A] uppercase tracking-[0.08em]">Logged by (RN / Admin name) *</label>
            <input
              type="text"
              value={loggedBy}
              onChange={(e) => setLoggedBy(e.target.value)}
              placeholder="e.g. Jane RN"
              className="tb-input mt-1"
              data-testid="pa-action-loggedby"
            />
          </div>

          {isApprove && (
            <div>
              <label className="text-[12px] font-semibold text-[#0B1E3A] uppercase tracking-[0.08em] inline-flex items-center gap-1">
                <Hash className="w-3 h-3" /> ForwardHealth PA # (if available)
              </label>
              <input
                type="text"
                value={paNumber}
                onChange={(e) => setPaNumber(e.target.value)}
                placeholder="e.g. 202604221234"
                className="tb-input mt-1 font-mono"
                data-testid="pa-action-panumber"
              />
              <div className="text-[11.5px] text-[#3A5275] mt-1">Optional — saves to the submission row for quick reference.</div>
            </div>
          )}

          {!isApprove && (
            <div>
              <label className="text-[12px] font-semibold text-[#0B1E3A] uppercase tracking-[0.08em]">Denial reason *</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="tb-input mt-1"
                data-testid="pa-action-reason"
              >
                <option value="">Select the reason ForwardHealth gave…</option>
                {DENIAL_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
              {chosenReason && (
                <div className="mt-2 rounded-lg bg-[#FFFDEC] border border-[#DBB90A] p-3 text-[12.5px] text-[#8A6B00] flex items-start gap-2" data-testid="pa-action-reason-tip">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{chosenReason.tip}</span>
                </div>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <label className="text-[12px] font-semibold text-[#0B1E3A] uppercase tracking-[0.08em]">
                {isApprove ? "Note (optional)" : "Detail / next steps (optional)"}
              </label>
              <VoiceDictateButton
                testid="pa-action-note-mic"
                label="Dictate"
                onResult={(text) => setNote((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))}
              />
            </div>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isApprove
                ? "e.g., approved for 14 hrs/week, cert 4/1–6/30."
                : "e.g., resubmit with updated ADL narrative by 5/15."
              }
              className="tb-textarea"
              data-testid="pa-action-note"
            />
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-[#D6E3EF] bg-[#F0F6FC] flex items-center justify-between gap-3">
          <div className="text-[11.5px] text-[#3A5275] inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            Timestamp: {new Date().toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-ghost" data-testid="pa-action-cancel">Cancel</button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className={isApprove ? "btn-primary" : "btn-primary !bg-[#c05746] !hover:bg-[#a7483a]"}
              data-testid="pa-action-submit"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                     : isApprove ? <><CheckCircle2 className="w-4 h-4" /> Mark approved</>
                                 : <><XCircle className="w-4 h-4" /> Mark denied</>}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
